<template>
  <v-app id="catalog-app" class="outfit-font">
    <AppLayout>
      <template #content>
        <v-card-text class="pt-0">

          <!--Promo Banners-->
          <PromoBanners
            v-if="ConfigShowFeaturedCards"
            @open-quiz="OpenQuizModal"
            @open-assistant="OpenAiModal"
            @show-deals="ShowPrinterOffers"
            @open-service-contract="service_contract_modal = true"
            @show-supplies="OpenShop"
          />

          <!-- Category Navigation Tabs -->
          <CategoryTabs
            v-if="available_category_tabs.length > 0"
            v-model="active_category_tab"
            :tabs="available_category_tabs">
          </CategoryTabs>

          <!--Sorter and Pagination-->
          <Flexed
            class="mb-3"
            justify="space-between"
            style="position: relative; z-index: 10;">
            <div style="color:rgba(0,0,0,0.6)" class="d-none d-md-block">{{ ActiveItemsShown }}</div>

            <!-- Mobile Category Select (<= 400px only, placed on opposite side of sort, meaning on its left) -->
            <div class="mobile-category-select-wrap" v-if="available_category_tabs.length > 1">
              <v-select
                rounded="lg"
                hide-details
                color="primary"
                density="compact"
                variant="outlined"
                v-model="active_category_tab"
                :items="mobile_category_select_items">
              </v-select>
            </div>

            <v-spacer/>

            <div class="custom-sorting-dropdown" v-click-outside="() => sortDropdownOpen = false">
              <div class="dropdown-trigger" @click="sortDropdownOpen = !sortDropdownOpen">
                <span>{{ activeSortTitle }}</span>
                <v-icon
                  size="20"
                  class="ml-1"
                  :icon="sortDropdownOpen ? 'mdi-menu-up' : 'mdi-menu-down'">
                </v-icon>
              </div>
              <transition name="dropdown-fade">
                <div v-show="sortDropdownOpen" class="dropdown-menu">
                  <div
                    v-for="s in sort_types"
                    :key="s.value ?? 'default'"
                    :class="['dropdown-item', { active: filters.sort_type === s.value }]"
                    @click="SetSortFilter(s.value); sortDropdownOpen = false"
                  >
                    {{ s.title }}
                  </div>
                </div>
              </transition>
            </div>
          </Flexed>

          <!--Product Card For Loop-->
          <v-row id="the-products" v-if="!is_fetching">
            <v-col :id="p_index==0?'first-product':''" v-for="(product,p_index) in PaginatedProducts" :key="p_index" cols="12" lg="3" md="3" sm="6" xs="12">
              <ProductCard :product="product"/>
            </v-col>
          </v-row>

          <v-divider style="margin-top:30px;margin-bottom:20px"/>

          <!--Pagination-->
          <v-pagination
            v-if="!is_fetching"
            class="catalog-pagination"
            :active-color="ConfigElementsColor"
            v-model="active_page"
            density="comfortable"
            rounded="lg"
            color="#00000080"
            :length="TotalPages">
          </v-pagination>
        </v-card-text>

        <IsFetchingData/>
        <NoCopiersAvail :data="FilteredProducts"/>
        <Configurator v-if="selected_copier && configurator"/>
        <ViewProduct/>
        <GhlChatWidget v-if="ConfigShowFeaturedCards"/>
        <ReviewProductForm v-if="review_modal"/>
        <ServiceContractModal v-model="service_contract_modal"/>
        <AskAiModal
          v-model="ask_ai_modal_open"
          :initial-mode="ask_ai_modal_mode">
        </AskAiModal>
      </template>
      <template #footer>
        <ShopLink/>
      </template>
    </AppLayout>

    <Alerts/>
    <CartTable/>
  </v-app>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useGlobalsStore } from '@/stores/globals'

// Imported Components
import AppLayout from "@/layouts/AppLayout.vue"
import Alerts from "@/components/misc/Alerts.vue"
import CartTable from "@/components/cart/CartTable.vue"
import ProductCard from "@/components/catalog/ProductCard.vue"
import Configurator from "@/components/configurator/Configurator.vue"
import IsFetchingData from "@/components/catalog/IsFetchingData.vue"
import NoCopiersAvail from "@/components/catalog/NoCopiersAvail.vue"
import ViewProduct from "@/components/catalog/ViewProduct.vue"
import ShopLink from "@/components/misc/ShopLink.vue"
import Flexed from "@/components/misc/Flexed.vue"
import GhlChatWidget from "@/components/assistant/GhlChatWidget.vue"
import ReviewProductForm from "@/components/configurator/ReviewProductForm.vue"
import PromoBanners from "@/components/catalog/PromoBanners.vue"
import ServiceContractModal from "@/components/catalog/ServiceContractModal.vue"
import CategoryTabs from "@/components/catalog/CategoryTabs.vue"
import AskAiModal from "@/components/catalog/AskAiModal.vue"
import { FilterProductByTab, type CategoryTabKey } from "@/mixins/CategoryMixins.ts"
import { MatchProductOfficeSize, MatchProductPrintVolume } from "@/mixins/ProductMixins.ts"

// Global Pinia Store
const store = useGlobalsStore()
const {
  filters,
  products,
  cart_items,
  is_fetching,
  configurator,
  manufacturers,
  printer_categories,
  categories,
  active_form,
  selected_copier,
  sort_types,
  is_mobile,
  review_modal,
  ConfigElementsColor,
  ConfigShowFeaturedCards,
  available_category_tabs
} = storeToRefs(store)

// Component Local State
const active_page = ref(1)
const sortDropdownOpen = ref(false)
const service_contract_modal = ref(false)
const ask_ai_modal_open = ref(false)
const ask_ai_modal_mode = ref<'ai' | 'quiz'>('ai')

function OpenAiModal() {
  ask_ai_modal_mode.value = 'ai'
  ask_ai_modal_open.value = true
}

function OpenQuizModal() {
  ask_ai_modal_mode.value = 'quiz'
  ask_ai_modal_open.value = true
}

const active_category_tab = computed<CategoryTabKey>({
  get() {
    const tab = filters.value.category_tab as CategoryTabKey
    const normalized = (tab === 'office-copiers' || tab === 'other' || !tab) ? 'printers' : tab
    if (available_category_tabs.value.length > 0 && !available_category_tabs.value.some(t => t.key === normalized)) {
      return (available_category_tabs.value[0]?.key ?? 'printers') as CategoryTabKey
    }
    return normalized
  },
  set(val: CategoryTabKey) {
    store.SetCategoryTab(val)
    active_page.value = 1
  }
})

// Auto-correct active tab if loaded catalog does not carry that category
watch(available_category_tabs, (tabs) => {
  if (tabs.length > 0 && !tabs.some(t => t.key === filters.value.category_tab)) {
    store.SetCategoryTab(tabs[0]?.key ?? 'printers')
    active_page.value = 1
  }
})

const mobile_category_select_items = computed(() => {
  return available_category_tabs.value.map(t => ({
    title: t.key === 'printers' ? 'Printers' : t.label,
    value: t.key
  }))
})

const activeSortTitle = computed(() => {
  const active = sort_types.value.find(s => s.value === filters.value.sort_type)
  return active ? active.title : 'Sort: Default Order'
})

// Methods
function DetectScreenSize() {
  is_mobile.value = window.innerWidth <= 768
}

function NormalizeSlug(text: string): string {
  return String(text || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function AddToCartFromURL() {
  const urlParams = new URLSearchParams(window.location.search)
  if (urlParams.has('add-to-cart')) {
    const product_id = urlParams.get('add-to-cart')
    const product = products.value.find(p => p.id == product_id)

    console.log("product: ", product)
    if (typeof product != 'undefined') {
      const current_items = [...cart_items.value]
      current_items.push({ product: product, datasets: [], quantity: 1 })
      store.SetCartItems(current_items)
      store.SetSuccess("1 item(s) added to quote")

      // We decide what type of form to show based on product
      const cat = String(product.printer_category || '').toLowerCase()
      const ff = String(product.form_factor || '').toLowerCase()
      if (product.manufacturer_id == 9) {
        active_form.value = 'mailing-form'
      }
      else if (ff == 'wide-format') {
        active_form.value = 'wide-format-form'
      }
      else if (cat.includes('phone')) {
        active_form.value = 'phone-form'
      }
      else if (cat == 'scanner') {
        active_form.value = 'scanner-form'
      }
       else {
        active_form.value = 'default-form'
      }
    } else {
      store.SetError("Sorry, this item is longer available.")
    }
  }
}

function ConfigureFromURL() {
  const urlParams = new URLSearchParams(window.location.search)
  const product_id = urlParams.get('configure') || urlParams.get('id')
  if (product_id) {
    const product = products.value.find(p => p.id == product_id)
    if (typeof product != 'undefined') {
      store.SetSelectedCopier(product)
      configurator.value = true
    } else {
      store.SetSelectedCopier(null)
      configurator.value = false
      store.SetError("Sorry, this item is longer available.")
    }
  }
}

function SetBrandFromURL() {
  const url_params = new URLSearchParams(window.location.search)
  const brand_param = url_params.get('brand') || url_params.get('manufacturer')
  if (brand_param !== null) {
    const brand = manufacturers.value.find(m => {
      const m_slug = NormalizeSlug(m.slug || m.name || '')
      return +m.id === +brand_param || m_slug === NormalizeSlug(brand_param)
    })
    if (brand !== undefined) {
      const brand_num = Number(brand.id)
      const current_manufacturers = [...(filters.value.manufacturers as number[])]
      if (!current_manufacturers.includes(brand_num)) {
        current_manufacturers.push(brand_num)
        store.SetFilters({
          ...filters.value,
          manufacturers: current_manufacturers
        })
      }
    }
  }
}

/**
 * Catches category/brand slugs from the URL query parameter (e.g. ?category=xerox, ?cat=printers)
 * and updates the active filters in the Pinia store.
 */
function SetCategoryFromURL() {
  const url_params = new URLSearchParams(window.location.search)

  const tab_param = url_params.get('tab')
  if (tab_param) {
    const tab_slug = NormalizeSlug(tab_param)
    const tab_map: Record<string, CategoryTabKey> = {
      'water-dispensers': 'water-dispensers',
      'water-dispenser': 'water-dispensers',
      'water': 'water-dispensers',
      'shredders': 'shredders',
      'shredder': 'shredders',
      'paper-shredders': 'shredders',
      'scanners': 'scanners',
      'scanner': 'scanners',
      'phones': 'phones',
      'phone': 'phones',
      'printers': 'printers',
      'copiers': 'printers',
      'office-copiers': 'printers',
      'other': 'printers'
    }
    if (tab_map[tab_slug]) {
      store.SetCategoryTab(tab_map[tab_slug])
      active_page.value = 1
    }
  }

  const category_param = url_params.get('category') || url_params.get('cat') || url_params.get('categories')

  if (!category_param) return

  const slugs = category_param.split(',').map(s => s.trim()).filter(Boolean)
  const current_filters = { ...filters.value }
  let filters_updated = false

  slugs.forEach(raw_slug => {
    const slug = NormalizeSlug(raw_slug)

    // Check direct tab matches
    if (slug === 'water-dispensers' || slug === 'water-dispenser') {
      store.SetCategoryTab('water-dispensers')
      active_page.value = 1
      return
    }
    if (slug === 'shredders' || slug === 'shredder' || slug === 'paper-shredders') {
      store.SetCategoryTab('shredders')
      active_page.value = 1
      return
    }
    if (slug === 'scanners' || slug === 'scanner') {
      store.SetCategoryTab('scanners')
      active_page.value = 1
      return
    }
    if (slug === 'phones' || slug === 'phone' || slug === 'ip-phones') {
      store.SetCategoryTab('phones')
      active_page.value = 1
      return
    }

    // 1. Check if slug matches a manufacturer (e.g. ?category=xerox, ?category=kyocera)
    const matched_manufacturer = manufacturers.value.find((m: any) => {
      const m_slug = NormalizeSlug(m.slug || m.name || '')
      const m_name = NormalizeSlug(m.name || '')
      return m_slug === slug || m_name === slug || String(m.id) === raw_slug
    })

    if (matched_manufacturer) {
      const man_id = Number(matched_manufacturer.id)
      const current_manufacturers: number[] = [...(current_filters.manufacturers as number[])]
      if (!current_manufacturers.includes(man_id)) {
        current_manufacturers.push(man_id)
        current_filters.manufacturers = current_manufacturers
        filters_updated = true
      }
      return
    }

    // 2. Check if slug matches a printer category (e.g. ?category=desktop-printers, ?category=copier)
    const matched_category = (printer_categories.value || []).find((cat: any) => {
      const cat_name = typeof cat === 'string' ? cat : (cat.name || cat.slug || '')
      const cat_slug = NormalizeSlug(cat_name)
      return cat_slug === slug || cat_name.toLowerCase() === raw_slug.toLowerCase()
    })

    if (matched_category) {
      const cat_val = typeof matched_category === 'string' ? matched_category : (matched_category.name || matched_category.slug)
      const current_categories: string[] = [...(current_filters.product_categories as string[])]
      if (!current_categories.includes(cat_val)) {
        current_categories.push(cat_val)
        current_filters.product_categories = current_categories
        filters_updated = true
      }
      return
    }

    // 3. Check general categories taxonomy list
    const matched_general_cat = (categories.value || []).find((c: any) => {
      const c_slug = NormalizeSlug(c.slug || c.name || '')
      return c_slug === slug || String(c.id) === raw_slug
    })

    if (matched_general_cat) {
      const cat_name = matched_general_cat.name || matched_general_cat.slug
      const current_categories: string[] = [...(current_filters.product_categories as string[])]
      if (!current_categories.includes(cat_name)) {
        current_categories.push(cat_name)
        current_filters.product_categories = current_categories
        filters_updated = true
      }
    }
  })

  if (filters_updated) {
    store.SetFilters(current_filters)
  }
}

function SetSortFilter(value: string | null) {
  const rawFilters = { ...filters.value }
  rawFilters.sort_type = value
  store.SetFilters(rawFilters)
}

function OpenShop() {
  window.open(store.ConfigShopURL, '_blank')
}

function ScrollToProductCards() {
  if (is_mobile.value) {
    document.querySelector('#the-products')?.scrollIntoView({ behavior: 'smooth' })
  }
}

function ShowPrinterOffers() {
  store.SetAnyState({ target: 'is_fetching', value: true })
  ScrollToProductCards()
  setTimeout(() => {
    const filtersCopy = { ...filters.value }
    filtersCopy.price_filters = [
      filtersCopy.price_options[0],
      filtersCopy.price_options[1]
    ]
    filtersCopy.device_types = ["Printer"]
    store.SetFilters(filtersCopy)
    store.SetAnyState({ target: 'is_fetching', value: false })
  }, 1000)
}

watch(filters, () => {
  active_page.value = 1
}, { deep: true })

// Computed Properties
const FilteredProducts = computed(() => {
  let filteredProducts = [...products.value]

  // Primary Category Tab Filter (Office copiers | Printers | Scanners | Phones | Other)
  if (filters.value.category_tab) {
    filteredProducts = filteredProducts.filter(product =>
      FilterProductByTab(product, filters.value.category_tab)
    )
  }

  if (filters.value.manufacturers.length > 0) {
    const manufacturerIds = filters.value.manufacturers as any[]
    filteredProducts = filteredProducts.filter(product =>
      manufacturerIds.includes(+product.manufacturer_id)
    )
  }
  if (filters.value.copier_colors.length > 0) {
    filteredProducts = filteredProducts.filter(product =>
      filters.value.copier_colors.includes(product.copier_color)
    )
  }
  if (filters.value.speed_minimum !== null && filters.value.speed_minimum >= 0 && filters.value.speed_maximum !== null && filters.value.speed_maximum > 0) {
    filteredProducts = filteredProducts.filter(product => {
      return (
        (product.speed_color < (filters.value.speed_maximum ?? 0) && product.speed_color > (filters.value.speed_minimum ?? 0)) ||
        (product.speed_black < (filters.value.speed_maximum ?? 0) && product.speed_black > (filters.value.speed_minimum ?? 0))
      )
    })
  }
  if (filters.value.paper_sizes.length > 0) {
    filteredProducts = filteredProducts.filter(product => {
      const paperSizesArray = Array.isArray(product.paper_size) ? product.paper_size : [product.paper_size]
      return filters.value.paper_sizes.some(size => paperSizesArray.includes(size))
    })
  }
  if (filters.value.product_categories.length > 0) {
    filteredProducts = filteredProducts.filter(product =>
      filters.value.product_categories.includes(product.printer_category)
    )
  }
  if (filters.value.form_factors.length > 0) {
    filteredProducts = filteredProducts.filter(product =>
      filters.value.form_factors.includes(product.form_factor)
    )
  }
  if (filters.value.capabilities.length > 0) {
    filteredProducts = filteredProducts.filter(product =>
      filters.value.capabilities.includes(product.capability)
    )
  }
  if (filters.value.connection_types.length > 0) {
    filteredProducts = filteredProducts.filter(product => {
      const product_connectivity = Array.isArray(product.connectivity) ? product.connectivity : [product.connectivity]
      return filters.value.connection_types.some(type => product_connectivity.includes(type))
    })
  }
  if (filters.value.usage_levels.length > 0) {
    filteredProducts = filteredProducts.filter(product =>
      filters.value.usage_levels.includes(product.usage_class)
    )
  }
  if (filters.value.office_sizes && filters.value.office_sizes.length > 0) {
    filteredProducts = filteredProducts.filter(product =>
      MatchProductOfficeSize(product, filters.value.office_sizes)
    )
  }
  if (filters.value.print_volumes && filters.value.print_volumes.length > 0) {
    filteredProducts = filteredProducts.filter(product =>
      MatchProductPrintVolume(product, filters.value.print_volumes)
    )
  }

  // Apply sorting
  if (filters.value.sort_type === 'ascending') {
    filteredProducts.sort((a, b) => a.name.localeCompare(b.name))
  }
  else if (filters.value.sort_type === 'descending') {
    filteredProducts.sort((a, b) => b.name.localeCompare(a.name))
  }
  /* Speed Sorter */
  else if (filters.value.sort_type === 'speed-low-high') {
    filteredProducts.sort((a, b) => {
      const speedA = a.speed_color > 0 ? a.speed_color : a.speed_black
      const speedB = b.speed_color > 0 ? b.speed_color : b.speed_black
      return speedA - speedB
    })
  }
  else if (filters.value.sort_type === 'speed-high-low') {
    filteredProducts.sort((a, b) => {
      const speedA = a.speed_color > 0 ? a.speed_color : a.speed_black
      const speedB = b.speed_color > 0 ? b.speed_color : b.speed_black
      return speedB - speedA
    })
  }
  else if (filters.value.sort_type === 'price-high-low') {
    filteredProducts.sort((a, b) => b.price - a.price)
  }
  else if (filters.value.sort_type === 'newest') {
    filteredProducts.sort((a, b) => {
      const dateA = a.released_date ? new Date(a.released_date).getTime() : 0
      const dateB = b.released_date ? new Date(b.released_date).getTime() : 0
      return dateB - dateA
    })
  }
  else if (filters.value.sort_type === 'oldest') {
    filteredProducts.sort((a, b) => {
      const dateA = a.released_date ? new Date(a.released_date).getTime() : 0
      const dateB = b.released_date ? new Date(b.released_date).getTime() : 0
      return dateA - dateB
    })
  }
  // Default sorting: sort ASC by product.id if no other sort_type is active
  else {
    filteredProducts.sort((a, b) => Number(a.id) - Number(b.id))
  }


  // If searching, apply search filter to the current filtered list
  if (filters.value.search_input.length > 0) {
    const searchTerm = filters.value.search_input.toLowerCase().trim()
    filteredProducts = filteredProducts.filter(product => {
      return (
        product.name.toLowerCase().includes(searchTerm) ||
        product.speed_black.toString().toLowerCase().includes(searchTerm) ||
        product.speed_color.toString().toLowerCase().includes(searchTerm) ||
        product.description.toLowerCase().includes(searchTerm) ||
        product.model_number.toLowerCase().includes(searchTerm)
      )
    })
  }

  return filteredProducts
})

const PaginatedProducts = computed(() => {
  const start = (active_page.value - 1) * filters.value.page_limit
  const end = start + filters.value.page_limit
  return FilteredProducts.value.slice(start, end)
})

const ActiveItemsShown = computed(() => {
  if (!FilteredProducts.value.length) return "0 of 0 items"

  const start = (active_page.value - 1) * filters.value.page_limit + 1
  let end = start + filters.value.page_limit - 1
  end = Math.min(end, FilteredProducts.value.length)

  return `Showing ${start}-${end} of ${FilteredProducts.value.length} Products`
})

const TotalPages = computed(() => {
  return Math.ceil(FilteredProducts.value.length / filters.value.page_limit)
})

// Watchers
watch([products, manufacturers], () => {
  AddToCartFromURL()
  ConfigureFromURL()
  SetBrandFromURL()
  SetCategoryFromURL()
})

// Lifecycle Hooks
store.FetchCatalogData()

onMounted(() => {
  // Set the AI Assistant config
  store.SetAiAssistantConfig()

  // Lifecycle Hooks
  store.FetchCatalogData()

  // Apply URL filters if data is already available
  SetBrandFromURL()
  SetCategoryFromURL()

  // Detect if device is mobile
  DetectScreenSize()
  window.addEventListener('resize', () => {
    DetectScreenSize()
  })

  // Start emitting to Analytics
  if (typeof (window as any).gtag === 'function') {
    (window as any).gtag('event', 'page_view', {
      page_path: '/products',
      page_title: 'Catalog Page'
    })
    console.log("Catalog now emits page visits to Analytics.")
  }
})
</script>
<style lang="scss">
.catalog-app {
  .pagination-wrap {
    max-width: 700px;
    margin: auto;
    margin-top: 30px;
  }

  .search-box.v-input--horizontal {
    margin: 0 !important;
    padding: 0 !important;
    min-height: 0;
    height: 40px;
  }
}
.custom-sorting-dropdown {
  position: relative;
  display: inline-block;
  user-select: none;
  z-index: 100;

  .dropdown-trigger {
    display: flex;
    align-items: center;
    font-weight: 500;
    font-size: 0.875rem;
    color: rgba(0, 0, 0, 0.87);
    cursor: pointer;
    padding: 6px 12px;
    border-radius: 6px;
    transition: background 0.15s ease;

    &:hover {
      background: rgba(0, 0, 0, 0.04);
    }
  }

  .dropdown-menu {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 4px;
    background: white;
    border-radius: 4px;
    box-shadow: 0 5px 5px -3px rgba(0,0,0,0.2), 0 8px 10px 1px rgba(0,0,0,0.14), 0 3px 14px 2px rgba(0,0,0,0.12);
    min-width: 220px;
    z-index: 110;
    padding: 8px 0;
  }

  .dropdown-item {
    padding: 8px 16px;
    font-size: 0.875rem;
    color: rgba(0, 0, 0, 0.87);
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
      background: rgba(0, 0, 0, 0.04);
    }

    &.active {
      background: rgba(26, 115, 232, 0.08);
      color: #1a73e8 !important;
      font-weight: 600;
    }
  }

  @media (max-width: 400px) {
    max-width: 52%;

    .dropdown-trigger {
      padding: 6px 8px;
      font-size: 0.825rem;
      justify-content: flex-end;

      span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }
}

.mobile-category-select-wrap {
  display: none;

  @media (max-width: 400px) {
    display: block;
    width: 130px;
    max-width: 46%;

    .v-field {
      border-radius: 8px !important;
      background: #f8fafc;
      font-size: 0.825rem;
    }

    .v-field__input {
      min-height: 36px !important;
      padding-top: 4px !important;
      padding-bottom: 4px !important;
      padding-left: 10px !important;
      font-size: 0.825rem;
      font-weight: 500;
    }

    .v-field__append-inner {
      padding-top: 4px !important;
      padding-bottom: 4px !important;
      align-items: center;
    }
  }
}

.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.dropdown-fade-enter-from,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.catalog-pagination {
  max-width: 500px;
  display: block;
  margin: auto;

  .v-pagination__item--is-active button {
    background-color: #1664C0 !important;
    color: #ffffff !important;
  }
}
</style>
